import React, { useState } from 'react';
import { Button, Empty, Input, Select, Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import styles from './modal.module.css';

const IDENTIFIERS = ['ean', 'barcode', 'config_sku', 'ql', 'sku', 'articlename'];
const PRODUCT = ['brand_name', 'brand', 'season', 'cg1', 'cg2', 'cg3', 'cg4', 'cg5', 'category'];
const ORDER = ['order_date', 'order_name', 'article_format', 'production_type', 'order_type', 'consumer', 'production_location'];

function groupOf(key) {
    const name = String(key || '').toLowerCase();
    if (IDENTIFIERS.includes(name)) return 'Identifiers';
    if (PRODUCT.includes(name)) return 'Product';
    if (ORDER.includes(name)) return 'Order & production';
    return 'Other';
}

function rowsFor(sku) {
    const rows = [];
    Object.keys(sku?.csvData || {}).forEach(key => {
        rows.push({ key, value: sku.csvData[key], source: 'csv' });
    });
    Object.keys(sku?.tagData || {}).forEach(key => {
        rows.push({ key, value: sku.tagData[key], source: 'tag' });
    });
    return rows;
}

export default function ClientDataTab({ sku, editing, updatedValues, csvFilterData, onStartEdit, onCancelEdit, onChange, onSave }) {
    const [query, setQuery] = useState('');
    const rows = rowsFor(sku);
    const q = query.trim().toLowerCase();
    const visible = rows.filter(row => !q || `${row.key} ${row.value ?? ''}`.toLowerCase().includes(q));
    const groups = ['Identifiers', 'Product', 'Order & production', 'Other']
        .map(title => ({ title, rows: visible.filter(row => groupOf(row.key) === title) }))
        .filter(group => group.rows.length);

    const optionsFor = key => {
        const match = (csvFilterData || []).find(item => item?.path === 'csvData' && item?.key === key);
        return (match?.values || []).map(value => ({ value, label: value }));
    };

    return (
        <div>
            <div className={styles.cardTitle}>
                <span>Client data</span>
                <Input allowClear placeholder="Search fields" value={query} onChange={event => setQuery(event.target.value)} style={{ maxWidth: 240 }} />
            </div>
            {!editing ? (
                <Button type="primary" onClick={onStartEdit} disabled={!Object.keys(sku?.csvData || {}).length} icon={<EditOutlined />} style={{ marginBottom: 12 }}>
                    Edit CSV values
                </Button>
            ) : null}
            {!groups.length ? <Empty description="No client data" /> : null}
            {groups.map(group => (
                <div key={group.title}>
                    <div className={styles.sectionTitle}>{group.title}</div>
                    <div className={styles.fields}>
                        {group.rows.map(row => {
                            const editingValue = row.source === 'csv' ? updatedValues?.[row.key] : row.value;
                            const shown = editing && row.source === 'csv' ? editingValue : row.value;
                            const empty = shown == null || shown === '';
                            return (
                                <div className={styles.field} key={`${row.source}-${row.key}`}>
                                    <label>{String(row.key).replace(/_/g, ' ')}</label>
                                    <small>
                                        {row.key}
                                        {row.source === 'tag' ? ' · tag' : ''}
                                    </small>
                                    {editing && row.source === 'csv' ? (
                                        <Select
                                            options={optionsFor(row.key)}
                                            showSearch
                                            style={{ minWidth: 160, width: '100%' }}
                                            value={editingValue}
                                            onChange={value => onChange(row, value)}
                                            allowClear
                                            onKeyUp={event => onChange(row, event.target.value)}
                                            notFoundContent={null}
                                        />
                                    ) : (
                                        <div className={`${styles.fieldValue} ${empty ? styles.emptyValue : ''}`}>{empty ? 'Not provided' : String(shown)}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            {editing ? (
                <Space style={{ marginTop: 16 }}>
                    <Button onClick={onCancelEdit}>Cancel</Button>
                    <Button type="primary" onClick={onSave}>
                        Update
                    </Button>
                </Space>
            ) : null}
        </div>
    );
}
